import React, { type PropsWithRef, type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Tooltip, { type TooltipProps } from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { SearchModeSwitcherProps } from '@atlassian/jira-jql-builder/src/common/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { messages } from './messages.tsx';
import { SearchModeToggleMenuItem } from './search-mode-toggle-menu-item/index.tsx';

type SearchModeOverflowMenuProp = SearchModeSwitcherProps & {
	defaultOpen?: boolean;
};

const TooltipWithDisplayInlineFix = (props: PropsWithChildren<TooltipProps>) => (
	// @ts-expect-error Incompatible ref type in tooltip (AK needs "HTMLElement", Compiled provides "HTMLDivElement" somehow these are not compatible in Typescript) */}
	<Tooltip tag={CustomTooltipTag} {...props} />
);

export const SearchModeOverflowMenu = ({
	defaultOpen = false,
	...searchModeSwitcherProps
}: SearchModeOverflowMenuProp): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOpenChange = ({ isOpen }: { isOpen: boolean }) => {
		const analyticsEvent = createAnalyticsEvent({
			action: isOpen ? 'opened' : 'closed',
			actionSubject: 'dropdown',
		});
		fireUIAnalytics(analyticsEvent, 'jqlBuilderAiSearchModeMenu');
	};

	const handlePrivacyAndLegalClicked = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});
		fireUIAnalytics(analyticsEvent, 'jqlBuilderAiPrivacyAndLegal');
	};

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<TooltipWithDisplayInlineFix
					hideTooltipOnClick
					content={formatMessage(messages.dropdownButtonTooltip)}
				>
					<Button
						{...props}
						spacing="none"
						appearance="subtle"
						iconBefore={
							<MoreIcon spacing="spacious" label={formatMessage(messages.overflowMenuLabel)} />
						}
						ref={triggerRef}
					/>
				</TooltipWithDisplayInlineFix>
			)}
			onOpenChange={handleOpenChange}
			defaultOpen={defaultOpen}
			spacing="compact"
			placement="bottom-end"
		>
			<DropdownItemGroup>
				<SearchModeToggleMenuItem {...searchModeSwitcherProps} />
				<DropdownItem
					elemBefore={<LockIcon spacing="spacious" label="" />}
					href="https://www.atlassian.com/trust/atlassian-intelligence"
					target="_blank"
					onClick={handlePrivacyAndLegalClicked}
				>
					{formatMessage(messages.dataAndPrivacy)}
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

// The following inline-flex fix is for making the "More" icon be aligned with the rest of the feedback buttons
// Prior to this change, this was aligned correctly only because the "More" button had a height of 32px (which is wrong according to the design)
// When the height is fixed, this is no longer working due to the built in styles of the Tooltip
// Hence we override the display property of the tag to inline-flex, so that it is aligned to the center
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomTooltipTag = styled.div<PropsWithRef<{}>>({
	display: 'inline-flex',
});
